// CTA "Hablar con la persona" + máquina de estados + indicador PERSISTENTE de audio abierto.
// Human-First: honesto (no afirma hablar con una persona si la atribución es ambigua), sin jerga
// SIP/RTP/SDP/extensión/codec/credenciales. Galarya NO graba la conversación.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { API, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import { esTecnica } from "@/lib/consolaV3/meta";
import { createAudioBridge } from "@/lib/consolaV3/audioBridge";
import { crearCierreAudio, resultadoTerminacion, cierreResuelto, entregarReserva } from "@/lib/consolaV3/cierreAudio";
import { Mic, MicOff, PhoneOff, Radio, Loader2, ShieldOff } from "lucide-react";

// UI-01A · se exporta para que el panel de recursos del protocolo explique la MISMA razón que ve
// el control de audio, en vez de afirmar "no disponible en este sitio" ante una indisponibilidad
// temporal. No cambia la lógica de audio: sólo comparte los textos ya existentes.
export const RAZON_NO_AUDIO = {
  SIN_DISPOSITIVO_AUDIO: "Audio no configurado para este sitio.",
  DISPOSITIVO_AUDIO_AMBIGUO: "Hay más de un dispositivo posible; no se puede abrir automáticamente.",
  AUDIO_NO_COMMISSIONED: "El audio de este dispositivo aún no está habilitado.",
  CONSENTIMIENTO_FALTANTE: "Falta el consentimiento de audio de esta persona.",
  PERSONA_AMBIGUA: "Este caso no permite identificar con quién se hablaría.",
  SIP_OFFLINE: "No se puede confirmar la conexión del dispositivo.",
  SIP_NO_CONFIRMADO: "No se puede confirmar la conexión del dispositivo.",
  // GC105 · una reserva activa NO prueba de quién es: el texto declara sólo lo que consta.
  AUDIO_DEVICE_BUSY: "El audio de este dispositivo ya tiene una sesión abierta.",
  CASO_NO_TOMADO: "Toma el caso para abrir audio.",
  NO_AUTORIZADO: "No tienes permiso para abrir audio en este caso.",
};

// GC105 · la autoría de la reserva sólo se afirma cuando el backend la declara (`sesion_activa_propia`).
export function razonAudio(avail) {
  if (!avail || avail.disponible) return "";
  if (avail.motivo === "AUDIO_DEVICE_BUSY") {
    if (avail.sesion_activa_propia === true) {
      return "Ya tienes una sesión de audio abierta con este dispositivo. Ciérrala para volver a intentarlo.";
    }
    if (avail.sesion_activa_propia === false) {
      return "Otro operador tiene abierta una sesión de audio con este dispositivo.";
    }
    return RAZON_NO_AUDIO.AUDIO_DEVICE_BUSY;
  }
  return RAZON_NO_AUDIO[avail.motivo] || "Audio no disponible ahora.";
}

// UI-01A · motivos por los que el audio NO está instalado/configurado para este caso (carencia
// estructural). Cualquier otro motivo es una indisponibilidad TEMPORAL de la sesión.
export const MOTIVOS_AUDIO_NO_INSTALADO = new Set([
  "SIN_DISPOSITIVO_AUDIO", "AUDIO_NO_COMMISSIONED", "CONSENTIMIENTO_FALTANTE",
]);

// Intento de terminación en el servidor: la AUTORIDAD sobre la liberación es suya. Vive fuera del
// componente para que el cierre del ciclo y la liberación de una reserva TARDÍA usen el mismo camino.
async function terminarEnBackend(sid) {
  try {
    const r = await api.post(`/sesiones-audio/${sid}/terminar`);
    return resultadoTerminacion(r?.status || 200);
  } catch (e) {
    // 403 = la ruta RECHAZÓ la solicitud antes de terminar nada: la reserva puede seguir activa.
    // 404 = ya no consta. Sin respuesta o 5xx = no hay evidencia de liberación.
    return resultadoTerminacion(e?.response?.status);
  }
}

export default function AudioTwoWay({ caso, demo, readOnly }) {  const a = caso || {};
  const taken = a.estado && a.estado !== "recibida" && a.estado !== "cerrada";
  const habilitable = !demo && !readOnly && !esTecnica(a) && a.atribucion !== "compartida" && a.atribucion !== "sin_asignar";

  const [avail, setAvail] = useState(null);      // {disponible, motivo}
  const [phase, setPhase] = useState("idle");    // idle | connecting | active | ending | error
  const [errMsg, setErrMsg] = useState("");
  const [cierreEstado, setCierreEstado] = useState(null);   // null | "rechazado" | "sin_confirmar"
  const bridgeRef = useRef(null);
  const audioRef = useRef(null);
  // GC105 · GENERACIÓN del ciclo: un evento TARDÍO del puente anterior no puede tocar una sesión
  // nueva (ni cerrarla, ni cambiar su fase).
  const genRef = useRef(0);
  const casoRef = useRef(a.id);     // caso VIGENTE: una respuesta tardía se reconoce por caso+generación
  const iniciandoRef = useRef(false);
  const montadoRef = useRef(true);

  // GC105 · CIERRE ÚNICO del ciclo terminal (colgado del dispositivo, error, cancelación voluntaria
  // y desmontaje). El identificador de la sesión vive aquí y sólo se suelta con evidencia.
  const cierreRef = useRef(null);
  if (cierreRef.current === null) {
    cierreRef.current = crearCierreAudio({
      detenerPuente: async () => {
        const b = bridgeRef.current;
        bridgeRef.current = null;
        if (b) await b.stop();
      },
      terminarEnBackend,
    });
  }

  const busyAudio = phase === "connecting" || phase === "active" || phase === "ending";

  useEffect(() => {
    if (!habilitable || !a.id || !taken) { setAvail(null); return; }
    let vivo = true;
    api.get(`/casos/${a.id}/audio/disponibilidad`)
      .then(({ data }) => { if (vivo) setAvail(data); })
      .catch(() => { if (vivo) setAvail(null); });
    return () => { vivo = false; };
  }, [a.id, taken, habilitable, phase]);

  // Vida del componente: tras desmontar no se tocan estados (el cierre sí se completa).
  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  // Al cambiar de caso o desmontar: cortar audio abierto (nunca dejar sesión eterna). El cierre pide
  // y acredita la liberación en el backend aunque la pantalla ya no exista.
  useEffect(() => {
    casoRef.current = a.id;
    return () => {
      genRef.current += 1;
      cierreRef.current.cerrar();
    };
  }, [a.id]);

  // Pestaña/navegador perdido: terminar best-effort (keepalive). Asterisk sigue siendo autoridad.
  useEffect(() => {
    const h = () => {
      const sid = cierreRef.current.sesion();
      if (!sid) return;
      try {
        fetch(`${API}/sesiones-audio/${sid}/terminar`, {
          method: "POST", keepalive: true,
          headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        });
      } catch (_) {}
    };
    window.addEventListener("pagehide", h);
    return () => window.removeEventListener("pagehide", h);
  }, []);

  function limpiarMedia() {
    if (audioRef.current) { try { audioRef.current.srcObject = null; } catch (_) {} }
  }

  // Camino terminal ÚNICO. `gen` ata el cierre al ciclo que lo pidió.
  async function cerrarCiclo(gen, { mensaje, error } = {}) {
    if (gen !== genRef.current) return;             // evento tardío de un ciclo anterior
    genRef.current += 1;
    const vigente = () => montadoRef.current && gen + 1 === genRef.current;
    if (vigente()) { setPhase("ending"); setCierreEstado(null); }
    const r = await cierreRef.current.cerrar();
    limpiarMedia();
    if (!vigente()) return;
    if (!cierreResuelto(r)) {
      // La reserva puede seguir viva: el ciclo NO se da por cerrado y se puede recuperar.
      setCierreEstado(r);
      genRef.current -= 1;
      toast.error(r === "rechazado"
        ? "El servidor no autorizó cerrar esta sesión de audio."
        : "No se pudo confirmar el cierre del audio. Vuelve a intentarlo.");
      return;
    }
    if (error) { setErrMsg(error); setPhase("error"); }
    else { setPhase("idle"); if (mensaje) toast.info(mensaje); }
  }

  function onBridgeState(gen, st) {
    if (gen !== genRef.current || !montadoRef.current) return;   // evento TARDÍO: se descarta
    if (st === "connecting") setPhase("connecting");
    else if (st === "active") setPhase("active");
    else if (st === "ended") cerrarCiclo(gen, { mensaje: "Audio finalizado." });
    else if (st && st.startsWith("error")) {
      cerrarCiclo(gen, { error: "Se perdió la conexión de audio." });
    }
  }

  async function iniciar() {
    if (iniciandoRef.current) return;      // dos clics/inicios rápidos: un solo intento de reserva
    iniciandoRef.current = true;
    try {
      await iniciarCiclo();
    } finally {
      iniciandoRef.current = false;
    }
  }

  async function iniciarCiclo() {
    if (cierreRef.current.pendiente()) { toast.info("Cerrando la sesión anterior…"); return; }
    // Una reserva anterior sin liberar NO se abandona: se reintenta antes de pedir otra.
    if (cierreRef.current.sesion()) {
      setPhase("ending");
      const previo = await cierreRef.current.cerrar();
      if (!cierreResuelto(previo)) {
        setCierreEstado(previo);
        setPhase("error");
        setErrMsg(previo === "rechazado"
          ? "Queda una sesión de audio anterior que el servidor no autorizó cerrar. Pide a un supervisor que la cierre."
          : "Queda una sesión de audio anterior sin cerrar confirmado. Vuelve a intentarlo en unos segundos.");
        return;
      }
    }
    setErrMsg("");
    setCierreEstado(null);
    setPhase("connecting");
    const gen = ++genRef.current;
    const ciclo = { gen, casoId: a.id };
    // 1) permiso de micrófono SOLO al iniciar (§12)
    let mic = null;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (e) {
      if (gen !== genRef.current) return;
      setPhase("error");
      setErrMsg(e && e.name === "NotAllowedError"
        ? "Permiso de micrófono denegado. Habilítalo para poder hablar."
        : "No se encontró un micrófono disponible.");
      return;
    } finally {
      // liberamos este stream de prueba; SIP.js abrirá el suyo al invitar
      if (mic) mic.getTracks().forEach((t) => t.stop());
    }
    // 2) reservar sesión en el backend (preflight + Asterisk en vivo + exclusión)
    let cfg = null;
    try {
      const { data } = await api.post(`/casos/${a.id}/audio/iniciar`);
      // La respuesta puede llegar TARDE (el operador cambió de caso o reinició el audio): entonces
      // esta reserva libera EXCLUSIVAMENTE su propia sesión y jamás toca la del ciclo vigente.
      const destino = await entregarReserva({
        sesionId: data.sesion_id,
        ciclo,
        vigente: { gen: genRef.current, casoId: casoRef.current },
        cierre: cierreRef.current,
        terminarEnBackend,
      });
      if (destino !== "adoptada") return;
      cfg = data.webrtc;
    } catch (e) {
      if (gen !== genRef.current) return;
      setPhase("error");
      const d = e?.response?.data?.detail;
      setErrMsg((d && d.mensaje) || formatApiErrorDetail(d) || "No se pudo abrir el audio.");
      return;
    }
    // 3) establecer WebRTC ↔ Asterisk
    try {
      const b = createAudioBridge(cfg, { onState: (st) => onBridgeState(gen, st), remoteAudio: audioRef.current });
      bridgeRef.current = b;
      await b.start();
    } catch (_) {
      await cerrarCiclo(gen, { error: "No se pudo establecer la conexión de audio." });
    }
  }

  async function terminar() {
    const gen = genRef.current;
    await cerrarCiclo(gen, { mensaje: null });
    if (montadoRef.current && !cierreRef.current.sesion()) toast.success("Audio terminado.");
  }

  if (!habilitable) return null;
  if (!taken) return null;

  const disponible = avail?.disponible;
  const razon = razonAudio(avail);

  return (
    <div className="mt-3" data-testid="v3-audio-twoway">
      <audio ref={audioRef} autoPlay data-testid="v3-audio-remote" className="hidden" />

      {phase === "idle" && (
        disponible ? (
          <Button data-testid="v3-btn-hablar" onClick={iniciar}
            className="bg-[#012472] hover:bg-[#012472]/90 text-white font-bold">
            <Mic strokeWidth={1.9} className="h-4 w-4 mr-2" /> Hablar por dispositivo
          </Button>
        ) : avail?.revalidable ? (
          // v2.9.10 — Self-recovery: el snapshot SIP puede estar stale. Permitir revalidar en vivo
          // (reserve hace live-probe ARI). NO afirma disponible; ofrece intentar y refrescar.
          <div className="inline-flex flex-col gap-1" data-testid="v3-audio-revalidable">
            <Button data-testid="v3-btn-verificar-audio" onClick={iniciar}
              className="bg-[#012472] hover:bg-[#012472]/90 text-white font-bold">
              <Mic strokeWidth={1.9} className="h-4 w-4 mr-2" /> Verificar e intentar el dispositivo
            </Button>
            <span className="text-[11px] text-slate-400">{razon} Se revalidará la conexión al intentar.</span>
          </div>
        ) : (
          <div data-testid="v3-audio-no-disponible"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
            <MicOff className="h-4 w-4 shrink-0" /> {razon || "Verificando disponibilidad de audio…"}
          </div>
        )
      )}

      {phase === "error" && (
        <div data-testid="v3-audio-error" className="flex items-center gap-2 rounded-lg border border-[#9B2C2C]/40 bg-[#9B2C2C]/10 px-3 py-2 text-sm font-medium text-[#9B2C2C] dark:text-[#F2B8B8]">
          <ShieldOff className="h-4 w-4 shrink-0" /> {errMsg}
          <button data-testid="v3-audio-reintentar" onClick={() => { setPhase("idle"); setErrMsg(""); }} className="ml-2 underline">Reintentar</button>
        </div>
      )}

      {/* Indicador PERSISTENTE — fijo al viewport, visible aunque el operador haga scroll (§11). */}
      {busyAudio && (
        <div data-testid="v3-audio-indicador"
          className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 flex flex-col items-center gap-1.5 rounded-2xl border-2 border-[#01CBE3] bg-[#012472] px-5 py-3 text-white shadow-2xl">
          <div className="flex items-center gap-3">
            {phase === "active" ? <Radio className="h-5 w-5 text-[#01CBE3] animate-pulse" /> : <Loader2 className="h-5 w-5 animate-spin text-[#01CBE3]" />}
            <div className="leading-tight">
              <p data-testid="v3-audio-estado" className="text-sm font-bold">
                {phase === "connecting" ? "Conectando…" : phase === "ending" ? "Terminando…" : "Audio abierto"}
              </p>
              <p className="text-[11px] text-[#BFE9F2]">{a.persona_nombre || (a.persona && a.persona.nombre_completo) || "Conversación con la persona"}</p>
            </div>
            <Button data-testid="v3-btn-terminar-audio" onClick={terminar}
              disabled={phase === "ending" && !cierreEstado}
              className="ml-2 bg-[#FE6A55] hover:bg-[#e85a46] text-white font-bold">
              <PhoneOff strokeWidth={2} className="h-4 w-4 mr-1.5" /> Terminar audio
            </Button>
          </div>
          {cierreEstado && (
            <p data-testid="v3-audio-cierre-sin-confirmar" data-estado={cierreEstado}
              className="max-w-[460px] text-center text-[11px] font-semibold text-[#FFD9D3]">
              {cierreEstado === "rechazado"
                ? "El servidor no autorizó cerrar esta sesión: puede seguir abierta. Pide a un supervisor que la cierre."
                : "No se pudo confirmar el cierre en el servidor. Vuelve a pulsar «Terminar audio»."}
            </p>
          )}
          <p className="text-[10px] uppercase tracking-wide text-[#7FCFE0]">Galarya no graba esta conversación.</p>
        </div>
      )}
    </div>
  );
}
